import { z } from 'zod';

import { YamlOrigin } from '@/generated/prisma/enums';
import {
  CUSTOM_RULE_DESCRIPTION_MAX_LENGTH,
  CUSTOM_RULE_TITLE_MAX_LENGTH,
} from '@/lib/ai-review-settings/constants';
import {
  CUSTOM_RULE_INSTRUCTION_FILE_PATH_MAX_LENGTH,
  CUSTOM_RULE_INSTRUCTION_FILE_PATHS_MAX_COUNT,
} from '@/lib/custom-rules/instruction-file-paths';
import {
  CUSTOM_RULE_INSTRUCTION_SOURCE,
  FILE_PATHS_CUSTOM_RULE_INSTRUCTION_SOURCE,
  TEXT_CUSTOM_RULE_INSTRUCTION_SOURCE,
} from '@/lib/custom-rules/instruction-sources';
import {
  RepositoryScopeInputSchema,
  RuleRepositoryScopeSchema,
} from '@/lib/repository-config/repository-scope';

export const CUSTOM_RULE_SOURCE_VALUES = ['ui', 'yaml'] as const;
export {
  CUSTOM_RULE_INSTRUCTION_FILE_PATH_MAX_LENGTH,
  CUSTOM_RULE_INSTRUCTION_FILE_PATHS_MAX_COUNT,
} from '@/lib/custom-rules/instruction-file-paths';
export type { CustomRuleInstructionSource } from '@/lib/custom-rules/instruction-sources';
export {
  CUSTOM_RULE_INSTRUCTION_SOURCE,
  DEFAULT_CUSTOM_RULE_INSTRUCTION_SOURCE,
  FILE_PATHS_CUSTOM_RULE_INSTRUCTION_SOURCE,
  TEXT_CUSTOM_RULE_INSTRUCTION_SOURCE,
} from '@/lib/custom-rules/instruction-sources';

export const CustomRuleManagedBySchema = z.enum(CUSTOM_RULE_SOURCE_VALUES);
export type CustomRuleManagedBy = z.infer<typeof CustomRuleManagedBySchema>;
export const CustomRuleInstructionSourceSchema = z.nativeEnum(CUSTOM_RULE_INSTRUCTION_SOURCE);

/** Provenance for YAML rules: 'repo_yaml' or 'org_yaml'. NULL for UI rules. */
export const YamlOriginSchema = z.nativeEnum(YamlOrigin).nullable();

// `z.nullable()` keeps the schema lightweight in client bundles compared to `z.union`
export const NullableStringSchema = z.string().nullable();

export const CustomRuleSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  enabled: z.boolean(),
  installationId: z.number(),
  repositoryId: z.number(),
  includePatterns: NullableStringSchema,
  excludePatterns: NullableStringSchema,
  category: NullableStringSchema,
  managedBy: CustomRuleManagedBySchema,
  instructionSource: CustomRuleInstructionSourceSchema,
  instructionFilePaths: z.array(z.string()),
  yamlHash: NullableStringSchema,
  lastSeenAt: NullableStringSchema,
  yamlOrigin: YamlOriginSchema,
  /**
   * Set on a repository's row of an organization agent (see rule-owner.ts). Optional so the many
   * places that build rule objects by hand need not set it.
   */
  organizationRuleId: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const CustomRulesResponseSchema = z.object({
  rules: z.array(CustomRuleSchema),
  maxEnabledRules: z.number().int().positive(),
  /**
   * Rows of organization agents that don't run on this repository because it hit the limit. A
   * response without it, such as one from before organization agents, means none.
   */
  notRunningRuleIds: z.array(z.string()).default([]),
});

export const CustomRuleResponseSchema = z.object({
  rule: CustomRuleSchema,
});

const CategorySchema = NullableStringSchema.refine(
  (value) =>
    value === null || (typeof value === 'string' && value.length >= 1 && value.length <= 50),
  'Category must be between 1 and 50 characters when provided',
);

const InstructionFilePathsSchema = z
  .array(z.string().trim().min(1).max(CUSTOM_RULE_INSTRUCTION_FILE_PATH_MAX_LENGTH))
  .min(1)
  .max(CUSTOM_RULE_INSTRUCTION_FILE_PATHS_MAX_COUNT);

const CommonCreateCustomRuleInputSchema = z.object({
  repositoryId: z.number(),
  title: z.string().min(1).max(CUSTOM_RULE_TITLE_MAX_LENGTH),
  enabled: z.boolean().optional(),
  includePatterns: NullableStringSchema.optional(),
  excludePatterns: NullableStringSchema.optional(),
  category: CategorySchema.optional(),
});

const TextDescriptionSchema = z
  .string()
  .min(1)
  .max(CUSTOM_RULE_DESCRIPTION_MAX_LENGTH)
  .refine((value) => value.trim().length > 0, {
    message: 'Text custom agents require a description',
  });

const TextCreateCustomRuleInputSchema = CommonCreateCustomRuleInputSchema.extend({
  description: TextDescriptionSchema,
  instructionSource: z.literal(TEXT_CUSTOM_RULE_INSTRUCTION_SOURCE).optional(),
  instructionFilePaths: z.undefined().optional(),
});

const FileBackedCreateCustomRuleInputSchema = CommonCreateCustomRuleInputSchema.extend({
  description: z.string().max(CUSTOM_RULE_DESCRIPTION_MAX_LENGTH).optional(),
  instructionSource: z.literal(FILE_PATHS_CUSTOM_RULE_INSTRUCTION_SOURCE),
  instructionFilePaths: InstructionFilePathsSchema,
});

export const CreateCustomRuleInputSchema = z.union([
  TextCreateCustomRuleInputSchema,
  FileBackedCreateCustomRuleInputSchema,
]);

export const BulkCreateCustomRuleInputSchema = z.object({
  repositoryIds: z.array(z.number()).min(1),
  title: z.string().min(1).max(CUSTOM_RULE_TITLE_MAX_LENGTH),
  description: z.string().min(1).max(CUSTOM_RULE_DESCRIPTION_MAX_LENGTH),
  includePatterns: NullableStringSchema.optional(),
  excludePatterns: NullableStringSchema.optional(),
  category: CategorySchema.optional(),
});

const UpdateCustomRuleInputBaseSchema = z.object({
  title: z.string().min(1).max(CUSTOM_RULE_TITLE_MAX_LENGTH).optional(),
  description: z.string().max(CUSTOM_RULE_DESCRIPTION_MAX_LENGTH).optional(),
  instructionSource: CustomRuleInstructionSourceSchema.optional(),
  instructionFilePaths: InstructionFilePathsSchema.optional(),
  enabled: z.boolean().optional(),
  includePatterns: NullableStringSchema.optional(),
  excludePatterns: NullableStringSchema.optional(),
  category: CategorySchema.optional(),
});

type UpdateCustomRuleInputBase = z.infer<typeof UpdateCustomRuleInputBaseSchema>;

export const UpdateCustomRuleInputSchema = UpdateCustomRuleInputBaseSchema.superRefine(
  addInstructionSourceUpdateIssues,
).refine((value) => Object.keys(value).length > 0, {
  message: 'At least one field must be provided to update',
});

function addInstructionSourceUpdateIssues(value: UpdateCustomRuleInputBase, ctx: z.RefinementCtx) {
  if (
    value.instructionSource === TEXT_CUSTOM_RULE_INSTRUCTION_SOURCE &&
    value.instructionFilePaths?.length
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['instructionFilePaths'],
      message: 'Text custom agents cannot include instruction file paths',
    });
  }
}

export const ResolveInstructionFilesInputSchema = z.object({
  repositoryId: z.number(),
  filePaths: InstructionFilePathsSchema,
  description: z.string().max(CUSTOM_RULE_DESCRIPTION_MAX_LENGTH).optional(),
});

export const InstructionFileWarningSchema = z.object({
  code: z.string(),
  path: z.string().optional(),
  message: z.string(),
  truncatedChars: z.number().optional(),
});

export const ResolveInstructionFilesResponseSchema = z.object({
  resolvedText: z.string(),
  usedChars: z.number(),
  remainingChars: z.number(),
  truncatedChars: z.number(),
  warnings: z.array(InstructionFileWarningSchema),
});

export const BulkUpdateCustomRulesInputSchema = z.object({
  ruleIds: z.array(z.string().min(1)).min(1),
  updates: UpdateCustomRuleInputSchema,
});

export const BulkDeleteCustomRulesInputSchema = z.object({
  ruleIds: z.array(z.string().min(1)).min(1),
});

export const BulkCreateCustomRulesResponseSchema = z.object({
  count: z.number(),
  repositoryIds: z.array(z.number()),
});

export const BulkMutationCountResponseSchema = z.object({
  count: z.number(),
});

/**
 * Where an organization agent runs today, counted over the installation's active repositories the
 * caller can see. Repositories the caller can't see stay out of every count: matching them against
 * patterns the caller chooses would let the counts spell out their names.
 */
export const OrganizationAgentReachSchema = z.object({
  /** Repositories the scope matches. */
  matchingCount: z.number().int().nonnegative(),
  /** Matching repositories where the agent fits under the agent limit. */
  runningCount: z.number().int().nonnegative(),
  /** Active repositories the caller can't see, left out of the counts. The same for every scope. */
  hiddenRepositoryCount: z.number().int().nonnegative(),
  /** A few matching repositories, for the card. */
  sampleNames: z.array(z.string()),
});

export const OrganizationAgentSchema = z.object({
  id: z.string(),
  installationId: z.number(),
  title: z.string(),
  description: z.string(),
  enabled: z.boolean(),
  includePatterns: NullableStringSchema,
  excludePatterns: NullableStringSchema,
  category: NullableStringSchema,
  repositoryScope: RuleRepositoryScopeSchema,
  /**
   * Names of the repositories the scope picks by id that the caller can read, keyed by id. A
   * picked repository missing here is hidden from the caller.
   */
  repositoryNames: z.record(z.string(), z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
  reach: OrganizationAgentReachSchema,
});

export const OrganizationAgentsResponseSchema = z.object({
  agents: z.array(OrganizationAgentSchema),
  /**
   * Always true: every installation can create organization agents. Clients built while they
   * rolled out to a few installations require this field, and long-open desktop windows run those
   * builds for days.
   */
  canCreate: z.literal(true),
});

export const OrganizationAgentResponseSchema = z.object({
  agent: OrganizationAgentSchema,
});

const OrganizationAgentFieldsSchema = z.object({
  title: z.string().trim().min(1).max(CUSTOM_RULE_TITLE_MAX_LENGTH),
  description: TextDescriptionSchema,
  enabled: z.boolean(),
  includePatterns: NullableStringSchema,
  excludePatterns: NullableStringSchema,
  category: CategorySchema,
  repositoryScope: RepositoryScopeInputSchema,
});

export const CreateOrganizationAgentInputSchema = OrganizationAgentFieldsSchema.partial({
  enabled: true,
  includePatterns: true,
  excludePatterns: true,
  category: true,
}).strict();

export const UpdateOrganizationAgentInputSchema = OrganizationAgentFieldsSchema.partial()
  .strict()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided to update',
  });

export const OrganizationAgentPreviewInputSchema = z
  .object({
    repositoryScope: RepositoryScopeInputSchema,
    /** The agent being edited, so it doesn't count toward its own limit. */
    ruleId: z.string().uuid().optional(),
  })
  .strict();

/** Where a draft scope would run an agent, counted like an agent's reach. */
export const OrganizationAgentPreviewResponseSchema = z.object({
  matchingCount: z.number().int().nonnegative(),
  /** Active repositories the caller can see. */
  totalCount: z.number().int().nonnegative(),
  /** Active repositories the caller can't see, left out of the counts. */
  hiddenRepositoryCount: z.number().int().nonnegative(),
  /** Matching repositories where the agent wouldn't run because they hit the agent limit. */
  atLimitNames: z.array(z.string()),
});

export type CustomRule = z.infer<typeof CustomRuleSchema>;
export type CustomRulesResponse = z.infer<typeof CustomRulesResponseSchema>;
export type CustomRuleResponse = z.infer<typeof CustomRuleResponseSchema>;
export type CreateCustomRuleInput = z.infer<typeof CreateCustomRuleInputSchema>;
export type BulkCreateCustomRuleInput = z.infer<typeof BulkCreateCustomRuleInputSchema>;
export type UpdateCustomRuleInput = z.infer<typeof UpdateCustomRuleInputSchema>;
export type BulkUpdateCustomRulesInput = z.infer<typeof BulkUpdateCustomRulesInputSchema>;
export type BulkDeleteCustomRulesInput = z.infer<typeof BulkDeleteCustomRulesInputSchema>;
export type BulkCreateCustomRulesResponse = z.infer<typeof BulkCreateCustomRulesResponseSchema>;
export type BulkMutationCountResponse = z.infer<typeof BulkMutationCountResponseSchema>;
export type ResolveInstructionFilesInput = z.infer<typeof ResolveInstructionFilesInputSchema>;
export type ResolveInstructionFilesResponse = z.infer<typeof ResolveInstructionFilesResponseSchema>;
export type OrganizationAgentReach = z.infer<typeof OrganizationAgentReachSchema>;
export type OrganizationAgent = z.infer<typeof OrganizationAgentSchema>;
export type OrganizationAgentsResponse = z.infer<typeof OrganizationAgentsResponseSchema>;
export type CreateOrganizationAgentInput = z.infer<typeof CreateOrganizationAgentInputSchema>;
export type UpdateOrganizationAgentInput = z.infer<typeof UpdateOrganizationAgentInputSchema>;
export type OrganizationAgentPreviewInput = z.infer<typeof OrganizationAgentPreviewInputSchema>;
export type OrganizationAgentPreviewResponse = z.infer<
  typeof OrganizationAgentPreviewResponseSchema
>;
